<template>
  <div class="max-w-5xl mx-auto mt-8 space-y-6">
    <div class="bg-white p-6 rounded-xl shadow-md flex items-start justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
          Assign Teams to Session
          <BaseInfoIcon :text="ERKLAERUNG.teamZuordnung" label="Assign Teams to Session" />
        </h2>
        <p class="text-gray-600 text-sm">Select a session and apply a saved team template to assign users, teams, and roles.</p>
      </div>
      <router-link to="/sessions/team-templates">
        <BaseButton class="bg-blue-500 hover:bg-blue-600">Manage Templates</BaseButton>
      </router-link>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-md space-y-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-2 font-semibold">Select Session</label>
          <BaseSelect v-model="selectedSessionId" :options="sessionOptions" />
          <div v-if="selectedSession" class="mt-2 space-y-1">
            <span v-if="selectedSession.has_subsessions_with_teams" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              ✓ Has subsessions with team assignments
            </span>
            <span v-if="selectedSession.has_team_assignments && selectedSession.parent_id" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              ✓ Inherited team assignments from parent
            </span>
          </div>
        </div>
        <div>
          <label class="block mb-2 font-semibold">Select Template</label>
          <BaseSelect v-model="selectedConstellationId" :options="constellationOptions" />
        </div>
      </div>

      <div class="flex items-center gap-3">
        <BaseButton
          class="bg-indigo-600 hover:bg-indigo-700"
          :disabled="!selectedSessionId || !selectedConstellationId || applying || (selectedSession && selectedSession.has_subsessions_with_teams)"
          @click="applyConstellation"
        >
          <span v-if="applying">Applying…</span>
          <span v-else>Apply to session</span>
        </BaseButton>
        <BaseInfoIcon :text="ERKLAERUNG.templateAnwenden" label="Apply to session" />
        <p v-if="selectedSession && selectedSession.has_subsessions_with_teams" class="text-sm text-red-600 font-medium">
          Cannot apply: This session has subsessions with team assignments.
        </p>
        <p v-else class="text-sm text-gray-500">Applies the template: assigns teams, users, and roles in one step.</p>
      </div>

      <div v-if="selectedConstellation" class="border rounded-lg p-4 bg-gray-50 space-y-3">
        <div class="flex items-center justify-between">
          <div>
            <p class="font-semibold text-gray-800">{{ selectedConstellation.name }}</p>
            <p class="text-sm text-gray-600" v-if="selectedConstellation.description">{{ selectedConstellation.description }}</p>
          </div>
          <span class="text-xs text-gray-500">{{ selectedConstellation.entries.length }} users</span>
        </div>

        <div v-if="selectedConstellation.entries.length" class="overflow-x-auto">
          <table class="min-w-full text-sm text-left text-gray-700">
            <thead>
              <tr class="text-gray-500">
                <th class="py-2">User</th>
                <th class="py-2">Teams</th>
                <th class="py-2">Role</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in selectedConstellation.entries" :key="entry.id" class="border-t">
                <td class="py-2">{{ userLabel(entry.userId) }}</td>
                <td class="py-2">
                  <span v-if="entry.teamIds && entry.teamIds.length">
                    {{ entry.teamIds.map(tid => teamLabel(tid)).join(', ') }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td class="py-2">{{ roleLabel(entry.roleId) || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-sm text-gray-500">No entries in this template.</p>
      </div>

      <div v-else class="text-sm text-gray-500">Select a template to preview its configuration.</div>
    </div>

    <div class="bg-white p-6 rounded-xl shadow-md space-y-4">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-800">Applied teams in this session</h3>
        <span class="text-sm text-gray-500">{{ sessionTeams.length }} teams</span>
      </div>

      <div v-if="sessionTeams.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="team in sessionTeams"
          :key="team.team_id"
          class="border rounded-xl p-4 bg-gray-50 shadow-sm"
        >
          <div class="flex items-center justify-between mb-3">
            <div>
              <p class="font-semibold text-gray-900">{{ team.team_name || team.team_id }}</p>
              <p class="text-xs text-gray-500">{{ usersForTeam(team.team_id).length }} members</p>
            </div>
            <div class="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold">
              {{ usersForTeam(team.team_id).length || 0 }}
            </div>
          </div>

          <div v-if="usersForTeam(team.team_id).length" class="space-y-2">
            <div
              v-for="user in usersForTeam(team.team_id)"
              :key="user.user_id"
              class="flex items-center justify-between rounded-lg bg-white border px-3 py-2"
            >
              <div class="flex items-center gap-2 text-gray-800">
                <span class="font-medium">{{ userLabel(user.user_id) }}</span>
              </div>
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                :class="user.role_name ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'"
              >
                {{ user.role_name || 'No role' }}
              </span>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500">No users currently assigned.</p>
        </div>
      </div>

      <p v-else class="text-sm text-gray-500">No teams applied yet for this session.</p>

      <div v-if="usersWithoutTeam.length" class="border rounded-xl p-4 bg-gray-50 shadow-sm">
        <div class="flex items-center justify-between mb-3">
          <div>
            <p class="font-semibold text-gray-900">No Team</p>
            <p class="text-xs text-gray-500">{{ usersWithoutTeam.length }} users</p>
          </div>
        </div>

        <div class="space-y-2">
          <div
            v-for="user in usersWithoutTeam"
            :key="user.user_id"
            class="flex items-center justify-between rounded-lg bg-white border px-3 py-2"
          >
            <div class="flex items-center gap-2 text-gray-800">
              <span class="font-medium">{{ userLabel(user.user_id) }}</span>
            </div>
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
              :class="user.role_name ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'"
            >
              {{ user.role_name || 'No role' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import { getSessions } from "@/live-sessions/api/sessions";
import { getTeams } from "@/live-sessions/api/teams";
import { getStudentUsers } from "@/api/users";
import { getRoles } from "@/live-sessions/api/roles";
import { listTeamConstellations, applyTeamConstellation } from "@/live-sessions/api/team_templates";
import { getTeamsForSession } from "@/live-sessions/api/team_sessions";
import { listUserSessions } from "@/live-sessions/api/usersessions";

export default {
  components: {
    BaseButton,
    BaseInfoIcon,
    BaseSelect,
  },

  data() {
    return {
      ERKLAERUNG,
      sessions: [],
      selectedSessionId: null,
      teams: [],
      users: [],
      roles: [],
      constellations: [],
      selectedConstellationId: null,
      applying: false,
      sessionTeams: [],
      sessionUsers: [],
    };
  },

  computed: {
    sessionOptions() {
      const flatten = (sessions, depth = 0) =>
        sessions.flatMap(s => [
          { 
            value: s.id.toString(), 
            label: `${"— ".repeat(depth)}${s.title}` 
          },
          ...(s.children ? flatten(s.children, depth + 1) : []),
        ]);
      return flatten(this.sessions);
    },
    selectedSession() {
      const findSession = (sessions) => {
        for (const s of sessions) {
          if (s.id === Number(this.selectedSessionId)) return s;
          if (s.children) {
            const found = findSession(s.children);
            if (found) return found;
          }
        }
        return null;
      };
      return findSession(this.sessions);
    },
    constellationOptions() {
      return this.constellations.map(c => ({ value: c.id.toString(), label: `${c.name} (${c.entries.length} users)` }));
    },
    selectedConstellation() {
      return this.constellations.find(c => c.id === Number(this.selectedConstellationId)) || null;
    },
    usersWithoutTeam() {
      return this.sessionUsers.filter(u => !u.team_ids || u.team_ids.length === 0);
    },
  },

  watch: {
    async selectedSessionId(newId) {
      if (newId) {
        localStorage.setItem('teamAssignment_selectedSessionId', newId);
      }
      await this.fetchSessionSnapshot(newId);
    },
    selectedConstellationId(newId) {
      if (newId) {
        localStorage.setItem('teamAssignment_selectedConstellationId', newId);
      }
    },
  },

  methods: {
    async fetchConstellations() {
      const res = await listTeamConstellations();
      const mapEntries = (entries = []) => entries.map(e => ({
        id: e.id,
        userId: Number(e.user_id),
        teamIds: (e.team_ids || []).map(id => Number(id)),
        roleId: e.role_id === null || e.role_id === undefined ? null : Number(e.role_id),
      }));

      this.constellations = (res.data || []).map(c => ({
        ...c,
        entries: mapEntries(c.entries),
      }));
    },

    // --- Sessions ---
    async fetchSessions() {
      const res = await getSessions();
      this.sessions = res.data;
      
      // Restore from localStorage or default to first session
      const savedSessionId = localStorage.getItem('teamAssignment_selectedSessionId');
      if (savedSessionId && this.sessionOptions.some(opt => opt.value === savedSessionId)) {
        this.selectedSessionId = Number(savedSessionId);
      } else if (this.sessions.length > 0) {
        this.selectedSessionId = this.sessions[0].id;
      }
    },

    async fetchSessionSnapshot(sessionId) {
      if (!sessionId) return;
      const [teamsRes, usersRes] = await Promise.all([
        getTeamsForSession(sessionId),
        listUserSessions(sessionId),
      ]);
      this.sessionTeams = teamsRes.data || [];
      this.sessionUsers = usersRes.data || [];
    },

    async fetchReferenceData() {
      const [teamsRes, usersRes, rolesRes] = await Promise.all([
        getTeams(),
        getStudentUsers(),
        getRoles(),
      ]);
      this.teams = teamsRes.data || [];
      this.users = usersRes.data || [];
      this.roles = rolesRes.data || [];
    },

    userLabel(id) {
      const u = this.users.find(user => user.id === id);
      return u ? `${u.first_name} ${u.last_name}` : "Unknown user";
    },
    teamLabel(id) {
      const t = this.teams.find(team => team.id === id);
      return t ? t.name : "Unknown team";
    },
    roleLabel(id) {
      const r = this.roles.find(role => role.id === id);
      return r ? r.name : null;
    },

    usersForTeam(teamId) {
      return this.sessionUsers.filter(u => u.team_ids && u.team_ids.includes(teamId));
    },

    async applyConstellation() {
      if (!this.selectedSessionId || !this.selectedConstellationId) return;
      this.applying = true;
      try {
        await applyTeamConstellation(this.selectedConstellationId, this.selectedSessionId);
        await this.fetchSessionSnapshot(this.selectedSessionId);
      } finally {
        this.applying = false;
      }
    },
  },

  async mounted() {
    await Promise.all([this.fetchConstellations(), this.fetchSessions(), this.fetchReferenceData()]);
    
    // Restore saved constellation
    const savedConstellationId = localStorage.getItem('teamAssignment_selectedConstellationId');
    if (savedConstellationId && this.constellations.some(c => c.id === Number(savedConstellationId))) {
      this.selectedConstellationId = savedConstellationId;
    }
    
    if (this.selectedSessionId) await this.fetchSessionSnapshot(this.selectedSessionId);
  },
};
</script>
